import useInfiniteScrollTable from 'common/components/BasicTable/useInfiniteScrollTable'
import PageWrapper from 'common/components/PageWrapper'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { fNumber } from 'common/utils/formatNumber'
import {
  INITIAL_PAGE_SIZE,
  useProductCategory,
  useProductsV3,
  useStockStatistic,
} from 'modules/inventory/api/queries'
import { ProductV3, ProductStockLevel } from 'modules/inventory/api/type'
import ProductTools from 'modules/inventory/components/ProductTools'
import { usePermitSomeActionToFeature, usePermitToActionFeature } from 'modules/user/api/selector'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PATH_DASHBOARD } from 'routes/paths'

import AddIcon from '@mui/icons-material/Add'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'

import NoDataNoBorder from 'components/NoDataNoBorder'
import CreateProductDialog from '../CreateProductPage'


const CATEGORY_COLORS = [
  { bg: '#E3F2FD', text: '#1565C0' },
  { bg: '#FFF3E0', text: '#E65100' },
  { bg: '#F3E5F5', text: '#6A1B9A' },
  { bg: '#E8F5E9', text: '#2E7D32' },
  { bg: '#FBE9E7', text: '#BF360C' },
  { bg: '#E0F2F1', text: '#00695C' },
  { bg: '#FCE4EC', text: '#880E4F' },
  { bg: '#F9FBE7', text: '#558B2F' },
]

function getCategoryColor(idx: number) {
  return CATEGORY_COLORS[idx % CATEGORY_COLORS.length]
}

function useDebounced<T>(value: T, delay = 600) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return debounced
}

const ProductPage = () => {
  const { t } = useTranslation('product')

  const STOCK_LEVEL_LABEL: Record<ProductStockLevel | '', string> = {
    '': t('inventory.all_products'),
    LOW_STOCK: t('inventory.low_stock'),
    OUT_OF_STOCK: t('inventory.out_of_stock'),
  }
  const theme = useTheme()
  const navigate = useNavigate()
  const showAddProduct = usePermitToActionFeature([CRUDAction.Create], FeatureKey.Inventory)
  const canManageProduct = usePermitSomeActionToFeature(
    [CRUDAction.Update, CRUDAction.Delete],
    FeatureKey.Inventory
  )

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedRow, setSelectedRow] = useState<any | null>(null)
  const open = Boolean(anchorEl)

  const [searchParams, setSearchParams] = useSearchParams()
  const [categoryId, setCategoryId] = useState<string>('')
  const [searchText, setSearchText] = useState('')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const debouncedSearch = useDebounced(searchText, 600)

  const rawLevel = searchParams.get('level')
  const stockLevel: ProductStockLevel | '' =
    rawLevel === 'LOW_STOCK' || rawLevel === 'OUT_OF_STOCK' ? rawLevel : ''

  const setStockLevel = (level: ProductStockLevel | '') => {
    if (level) {
      setSearchParams({ level })
    } else {
      setSearchParams({})
    }
  }

  const { data: statistic } = useStockStatistic({
    categoryId: categoryId || undefined,
    productName: debouncedSearch.trim() || undefined,
  })
  const { data: categoryData } = useProductCategory()
  const categories = categoryData ?? []

  const {
    data: productsData,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useProductsV3({
    categoryId: categoryId || undefined,
    productName: debouncedSearch.trim() || undefined,
    productStockLevel: stockLevel || undefined,
    productStockStatus: 'ACTIVE',
    pageSize: INITIAL_PAGE_SIZE,
  })

  const products = productsData?.pages.flatMap((p) => p.products).filter(Boolean) ?? []

  const handleScroll = useInfiniteScrollTable({
    isLoading,
    onLoading: () => { if (hasNextPage) fetchNextPage() },
  })

  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>, row: ProductV3) => {
    setAnchorEl(e.currentTarget)
    setSelectedRow(row)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setSelectedRow(null)
  }

  const isFiltered = stockLevel !== ''
  const pageTitle = STOCK_LEVEL_LABEL[stockLevel]

  const LINKS = [
    { name: t('inventory.title'), href: PATH_DASHBOARD.inventory.product },
    ...(isFiltered ? [{ name: pageTitle }] : []),
  ]

  const StatCard = ({
    icon,
    count,
    label,
    bgColor,
    bgIcon,
    textColor,
    level,
  }: {
    icon: React.ReactNode
    count?: number
    label: string
    bgColor: string
    bgIcon: string
    textColor: string
    level?: ProductStockLevel | ''
  }) => (
    <Box
      onClick={() => level !== undefined && setStockLevel(level)}
      sx={{
        flex: 1,
        minWidth: 180,
        bgcolor: bgColor,
        borderRadius: 3,
        p: 2.5,
        cursor: level !== undefined ? 'pointer' : 'default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        border: stockLevel === level && level !== '' ? `2px solid ${textColor}` : '2px solid transparent',
        transition: 'box-shadow 0.15s',
        '&:hover': level !== undefined ? { boxShadow: `0 4px 12px ${textColor}33` } : {},
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: bgIcon,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: textColor, lineHeight: 1 }}>
          {count ?? '-'}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="flex-end">
          <Typography variant="body2" sx={{ color: textColor, fontWeight: 500 }}>
            {label}
          </Typography>
          {level !== undefined && level !== '' && (
            <Typography variant="body2" sx={{ color: textColor }}>›</Typography>
          )}
        </Stack>
      </Box>
    </Box>
  )

  return (
    <PageWrapper
      title={
        isFiltered ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton size="small" onClick={() => setStockLevel('')}>
              <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Typography variant="h4">{pageTitle}</Typography>
          </Stack>
        ) : t('inventory.title')
      }
      titleHead={isFiltered ? pageTitle : t('inventory.title')}
      links={LINKS}
      action={
        isFiltered && stockLevel === 'OUT_OF_STOCK' ? (
          <Alert
            severity="error"
            variant="outlined"
            sx={{
              maxWidth: 540,
              borderRadius: '12px',
              bgcolor: '#FFF5F5',
              borderColor: '#FFA3A3',
              alignItems: 'center',
              p: 1,
              '& .MuiAlert-icon': {
                color: '#FF0000',
                fontSize: 28,
                mr: 2,
              },
              '& .MuiAlert-message': {
                p: 0,
              },
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: '#FF0000', fontWeight: 700, fontSize: '15px', mb: 0.5 }}
            >
              {t('inventory.out_of_stock_alert.title')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#5C5C5C', fontWeight: 400, fontSize: '13px' }}
            >
              {t('inventory.out_of_stock_alert.desc')}
            </Typography>
          </Alert>
        ) : null
      }
    >
      {/* Action buttons row — hidden when filtered */}
      {!isFiltered && (
        <Stack direction={{ xs: 'column', md: 'row' }} sx={{
          mb: 3, justifyContent: { sm: 'space-between' },
        }}>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<HistoryOutlinedIcon />}
              onClick={() => navigate(PATH_DASHBOARD.inventory.stock)}
              sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 500, backgroundColor: 'white' }}
            >
              {t('inventory.stock_history')}
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<SettingsOutlinedIcon />}
              onClick={() => navigate(PATH_DASHBOARD.inventory.settings)}
              sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 500, backgroundColor: 'white' }}
            >
              {t('inventory.settings')}
            </Button>
          </Stack>

          <Stack >
            {showAddProduct && (
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 500 }}
              >
                {t('inventory.add_product')}
              </Button>
            )}
          </Stack>
        </Stack>
      )}

      {/* Category filter row */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip
          label={t('inventory.all')}
          clickable
          onClick={() => { setCategoryId(''); if (!isFiltered) setStockLevel('') }}
          sx={{
            fontWeight: 600,
            bgcolor: categoryId === '' ? 'primary.main' : 'white',
            color: categoryId === '' ? 'white' : 'text.primary',
            '&:hover': { bgcolor: categoryId === '' ? 'primary.dark' : 'grey.200' },
          }}
        />
        {categories.map((cat, idx) => {
          const color = getCategoryColor(idx)
          const selected = categoryId === cat.id
          return (
            <Chip
              key={cat.id}
              label={cat.name}
              clickable
              onClick={() => { setCategoryId(selected ? '' : cat.id) }}
              sx={{
                fontWeight: 600,
                bgcolor: selected ? 'primary.main' : 'white',
                color: selected ? 'white' : 'text.primary',
                '&:hover': { bgcolor: 'primary.dark', color: 'white' },
              }}
            />
          )
        })}
      </Stack>

      {/* Stats row — hidden when filtered */}
      {!isFiltered && (
        <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
          <StatCard
            icon={<Box
              component="img"
              src="/static/icons/inventory/all_stock.svg"
              alt="All"
              sx={{ width: 24, height: 24 }}
            />}
            count={statistic?.product_count}
            label={t('inventory.all_products')}
            bgColor="white"
            bgIcon="primary.main"
            textColor="black"
            level=""
          />
          <StatCard
            icon={<Box
              component="img"
              src="/static/icons/inventory/low_stock.svg"
              alt="All"
              sx={{ width: 24, height: 24 }}
            />}
            count={statistic?.product_low_stock_count_active}
            label={t('inventory.low_stock')}
            bgColor="#FFF7CD"
            bgIcon="#C07A00"
            textColor="#C07A00"
            level="LOW_STOCK"
          />
          <StatCard
            icon={<Box
              component="img"
              src="/static/icons/inventory/out_stock.svg"
              alt="All"
              sx={{ width: 24, height: 24 }}
            />}
            count={statistic?.product_out_of_stock_count_active}
            label={t('inventory.out_of_stock')}
            bgColor="#FFF5F5"
            bgIcon="#B72136"
            textColor="#B72136"
            level="OUT_OF_STOCK"
          />
        </Stack>
      )}

      {/* Table */}
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100vh',
          bgcolor: 'common.white',
          borderRadius: '24px',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ color: theme.palette.primary.dark, fontWeight: 600 }}>
            {t('product.detail')}
          </Typography>
          <TextField
            size="small"
            placeholder={t('inventory.search')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{
              width: 280,
              bgcolor: 'common.white',
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchRoundedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {isLoading ? (
          <NoDataNoBorder />
        ) : products.length > 0 ? (
          <TableContainer
            component={Paper}
            onScroll={handleScroll}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            <Table
              stickyHeader
              sx={{
                borderCollapse: 'separate',
                borderSpacing: 0,
                '& thead th': {
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.palette.common.black,
                  backgroundColor: theme.palette.grey[100],
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
                '& tbody td': { borderBottom: '1px solid', borderColor: 'divider' },
                '& thead th:first-of-type': { borderTopLeftRadius: 12 },
                '& thead th:last-of-type': { borderTopRightRadius: 12 },
                '& tbody tr:last-of-type td:first-of-type': { borderBottomLeftRadius: 12 },
                '& tbody tr:last-of-type td:last-of-type': { borderBottomRightRadius: 12 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 200 }}>{t('title.product')}</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>{t('product.category')}</TableCell>
                  {!isFiltered && (
                    <>
                      <TableCell align="left" sx={{ minWidth: 100 }}>{t('inventory.lot_count')}</TableCell>
                      <TableCell align="right" sx={{ minWidth: 150 }}>{t('inventory.price_per_unit')}</TableCell>
                    </>
                  )}
                  <TableCell align="right" sx={{ minWidth: 120 }}>{t('product.remaining')}</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((row: ProductV3, idx: number) => {
                  const catIdx = categories.findIndex((c) => c.id === row.category_id)
                  const catColor = getCategoryColor(catIdx >= 0 ? catIdx : idx)
                  const isNegative = row.stock_quantity_active < 0
                  return (
                    <TableRow
                      key={row.product_id || idx}
                      hover
                      onClick={() => navigate(`/dashboard/product/${row.product_id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            src={row.product_img_url || undefined}
                            alt={row.product_name}
                            variant="rounded"
                            sx={{ width: 32, height: 32, bgcolor: '#E5FFF2', color: '#00AB55', fontSize: 22, fontWeight: 700 }}
                          >
                            {row.product_name?.[0]}
                          </Avatar>
                          <Typography variant="body2" fontWeight={500}>{row.product_name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {row.category_name ? (
                          <Chip
                            label={row.category_name}
                            size="small"
                            sx={{
                              bgcolor: catColor.bg,
                              color: catColor.text,
                              fontWeight: 600,
                              fontSize: 12,
                              height: 24,
                              borderRadius: 1,
                            }}
                          />
                        ) : '-'}
                      </TableCell>
                      {!isFiltered && (
                        <>
                          <TableCell align='left'>
                            <Typography variant="body2">{row.stock_count ?? 0}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {row.stock_latest_price_per_unit_active
                                ? t('price.per.unit.value', { price: fNumber(row.stock_latest_price_per_unit_active), unit: row.unit_abbreviation })
                                : '-'}
                            </Typography>
                          </TableCell>
                        </>
                      )}
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ color: isNegative ? theme.palette.error.main : 'text.primary', fontWeight: isNegative ? 600 : 400 }}
                        >
                          {fNumber(row.stock_quantity_active) ?? 0} {row.unit_abbreviation}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {canManageProduct && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, row)}
                            id={`basic-button-${row.product_id}`}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>

                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <NoDataNoBorder />
        )}
      </Card>
      <ProductTools
        onClose={handleCloseMenu}
        open={open}
        anchorEl={anchorEl}
        productId={selectedRow?.product_id}
        productItem={selectedRow}
        pageSize={INITIAL_PAGE_SIZE}
      />
      <CreateProductDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </PageWrapper>
  )
}

export default ProductPage
